import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../context/SearchContext';
import { format } from 'date-fns';
import './SearchBar.css';

const SearchBar = ({ compact = false }) => {
  const { searchState, dispatch } = useSearch();
  const navigate = useNavigate();
  const [showCalendar, setShowCalendar] = useState(false);
  const [showGuests, setShowGuests] = useState(false);
  const [localCity, setLocalCity] = useState(searchState.city || '');
  const [guests, setGuests] = useState({
    adults: searchState.adults || 2,
    children: searchState.children || 0,
    rooms: searchState.rooms || 1
  });
  const [dates, setDates] = useState({
    checkIn: searchState.checkIn || new Date(),
    checkOut: searchState.checkOut || new Date(new Date().setDate(new Date().getDate() + 2))
  });
  const calendarRef = useRef(null);
  const guestsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) setShowCalendar(false);
      if (guestsRef.current && !guestsRef.current.contains(e.target)) setShowGuests(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    dispatch({
      type: 'NEW_SEARCH',
      payload: { city: localCity, ...dates, ...guests }
    });
    navigate(`/hotels?city=${localCity}&adults=${guests.adults}&children=${guests.children}&rooms=${guests.rooms}`);
  };

  const updateGuest = (key, delta) => {
    setGuests(prev => ({
      ...prev,
      [key]: Math.max(key === 'adults' ? 1 : 0, (prev[key] || 0) + delta)
    }));
  };

  const nights = Math.round((dates.checkOut - dates.checkIn) / (1000 * 60 * 60 * 24));

  return (
    <div className={`searchbar ${compact ? 'searchbar-compact' : ''}`}>
      <div className="searchbar-inner">
        {/* Destination */}
        <div className="search-field">
          <label className="search-label">
            <span>📍</span> Destination
          </label>
          <input
            type="text"
            placeholder="Where are you going?"
            value={localCity}
            onChange={(e) => setLocalCity(e.target.value)}
            className="search-input"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>

        <div className="search-divider" />

        {/* Dates */}
        <div className="search-field search-dates" ref={calendarRef}>
          <label className="search-label">
            <span><i class="fa-solid fa-calendar-days"></i></span> Dates
          </label>
          <button
            className="search-input search-date-btn"
            onClick={() => { setShowCalendar(!showCalendar); setShowGuests(false); }}
          >
            {format(dates.checkIn, 'MMM d')} – {format(dates.checkOut, 'MMM d')}
            <span className="nights-badge">{nights}n</span>
          </button>
          {showCalendar && (
            <div className="date-picker-dropdown">
              <SimpleDatePicker dates={dates} setDates={setDates} onClose={() => setShowCalendar(false)} />
            </div>
          )}
        </div>

        <div className="search-divider" />

        {/* Guests */}
        <div className="search-field" ref={guestsRef}>
          <label className="search-label">
            <span><i class="fa-solid fa-users"></i></span> Guests
          </label>
          <button
            className="search-input search-date-btn"
            onClick={() => { setShowGuests(!showGuests); setShowCalendar(false); }}
          >
            {guests.adults + guests.children} guest{guests.adults + guests.children !== 1 ? 's' : ''} · {guests.rooms} room{guests.rooms !== 1 ? 's' : ''}
          </button>
          {showGuests && (
            <div className="guests-dropdown">
              {[
                { key: 'adults', label: 'Adults', sub: '13+ years' },
                { key: 'children', label: 'Children', sub: '0–12 years' },
                { key: 'rooms', label: 'Rooms', sub: '' }
              ].map(({ key, label, sub }) => (
                <div key={key} className="guest-row">
                  <div>
                    <p className="guest-label">{label}</p>
                    {sub && <p className="guest-sub">{sub}</p>}
                  </div>
                  <div className="guest-counter">
                    <button onClick={() => updateGuest(key, -1)} className="counter-btn">−</button>
                    <span className="counter-val">{guests[key]}</span>
                    <button onClick={() => updateGuest(key, 1)} className="counter-btn">+</button>
                  </div>
                </div>
              ))}
              <button className="btn btn-dark btn-sm" style={{width:'100%', marginTop:'8px'}} onClick={() => setShowGuests(false)}>Done</button>
            </div>
          )}
        </div>

        {/* Search button */}
        <button className="search-btn" onClick={handleSearch}>
          <span><i class="fa-solid fa-magnifying-glass"></i></span>
          <span>Search</span>
        </button>
      </div>
    </div>
  );
};

// Simple inline date picker
const SimpleDatePicker = ({ dates, setDates, onClose }) => {
  const [selecting, setSelecting] = useState('checkIn');
  const [hovered, setHovered] = useState(null);
  const [viewMonth, setViewMonth] = useState(new Date(dates.checkIn));

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const totalDays = daysInMonth(year, month);
  const startDay = firstDayOfMonth(year, month);

  const handleDayClick = (day) => {
    const clicked = new Date(year, month, day);
    if (selecting === 'checkIn') {
      setDates({ checkIn: clicked, checkOut: new Date(clicked.getTime() + 86400000) });
      setSelecting('checkOut');
    } else {
      if (clicked > dates.checkIn) {
        setDates(prev => ({ ...prev, checkOut: clicked }));
        onClose();
        setSelecting('checkIn');
      } else {
        setDates({ checkIn: clicked, checkOut: new Date(clicked.getTime() + 86400000) });
        setSelecting('checkOut');
      }
    }
  };

  const isInRange = (day) => {
    const d = new Date(year, month, day);
    const end = selecting === 'checkOut' && hovered ? hovered : dates.checkOut;
    return d > dates.checkIn && d < end;
  };

  const isStart = (day) => new Date(year, month, day).toDateString() === dates.checkIn.toDateString();
  const isEnd = (day) => new Date(year, month, day).toDateString() === dates.checkOut.toDateString();
  const isPast = (day) => new Date(year, month, day) < new Date(new Date().setHours(0,0,0,0));

  const prevMonth = () => setViewMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setViewMonth(new Date(year, month + 1, 1));

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  return (
    <div className="mini-calendar">
      <div className="calendar-header">
        <button onClick={prevMonth} className="cal-nav">‹</button>
        <span className="cal-title">{monthNames[month]} {year}</span>
        <button onClick={nextMonth} className="cal-nav">›</button>
      </div>
      <div className="cal-instruction">
        {selecting === 'checkIn' ? 'Select check-in date' : 'Select check-out date'}
      </div>
      <div className="calendar-grid">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} className="cal-day-name">{d}</div>
        ))}
        {Array.from({ length: startDay }, (_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: totalDays }, (_, i) => {
          const day = i + 1;
          const past = isPast(day);
          return (
            <button
              key={day}
              className={`cal-day ${isStart(day) ? 'cal-start' : ''} ${isEnd(day) ? 'cal-end' : ''} ${isInRange(day) ? 'cal-range' : ''} ${past ? 'cal-past' : ''}`}
              onClick={() => !past && handleDayClick(day)}
              onMouseEnter={() => setHovered(new Date(year, month, day))}
              disabled={past}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SearchBar;
